
import * as React from 'react';
import {
  Table as STable,
  TableBody as STableBody,
  TableRow as STableRow,
  TableFooter as STableFooter,
  TableHeader as STableHeader,
  TableHeaderItem as STableHeaderItem,
} from '@emorg-prd/standard-react';
import './Table.css'

type ReactNode = React.ReactNode;

const TABLE_PART = Symbol('TABLE_PART');
type TablePartTag = 'HEAD' | 'HEAD_ROW' | 'HEAD_CELL' | 'BODY' | 'ROW';
type WithTablePart<T> = T & { [TABLE_PART]?: TablePartTag };

const markPart = <P extends object>(Comp: React.FC<P>, part: TablePartTag) => {
  (Comp as WithTablePart<typeof Comp>)[TABLE_PART] = part;
  return Comp as React.FC<P> & { [TABLE_PART]: TablePartTag };
};

const SlotHead: React.FC<{ children?: ReactNode }> = () => null;
SlotHead.displayName = 'Table.Head';
markPart(SlotHead, 'HEAD');

const SlotHeadRow: React.FC<{ children?: ReactNode }> = () => null;
SlotHeadRow.displayName = 'Table.Head.Row';
markPart(SlotHeadRow, 'HEAD_ROW');

const SlotHeadCell: React.FC<{
  children?: ReactNode;
  className?: string;
  style?: React.CSSProperties;
}> = () => null;
SlotHeadCell.displayName = 'Table.Head.Cell';
markPart(SlotHeadCell, 'HEAD_CELL');

const SlotBody: React.FC<{ children?: ReactNode }> = () => null;
SlotBody.displayName = 'Table.Body';
markPart(SlotBody, 'BODY');

const SlotRow: React.FC<{
    children?: ReactNode;
    onClick?: React.MouseEventHandler<HTMLTableRowElement> | (() => void);
    key?: React.Key;
    style?: React.CSSProperties;
    verticalAlign?: 'top' | 'middle' | 'bottom';
    className?: string;
}> = ({ children, onClick, style, verticalAlign, className }) => (
    <tr
        className={`em-c-table__row${verticalAlign ? ` em-c-table__row--valign-${verticalAlign}` : ''}${className ? ` ${className}` : ''}`}
        style={style}
        onClick={onClick as any}
    >
        {React.Children.toArray(children).map((child, index) =>
            React.isValidElement(child) && child.type === SlotCell ? (
                <React.Fragment key={index}>{child}</React.Fragment>
            ) : (
                <td key={index} className="em-c-table__cell em-js-cell em-js-cell-editable">
                    {child}
                </td>
            )
        )}
    </tr>
);
SlotRow.displayName = 'Table.Row';
markPart(SlotRow, 'ROW');

const SlotCell: React.FC<{
    children?: ReactNode;
    colSpan?: number;
    className?: string;
    style?: React.CSSProperties;
    title?: string;
}> = ({ children, colSpan, className, style, title }) => (
    <td
        colSpan={colSpan}
        className={`em-c-table__cell em-js-cell${className ? ` ${className}` : ''}`}
        style={style}
        title={title}
    >
        {children}
    </td>
);
SlotCell.displayName = 'Table.Cell';

interface RootTableProps extends React.HTMLAttributes<HTMLDivElement> {
  header?: React.ReactNode;
  footer?: React.ReactElement;
  children?: React.ReactNode;
  hasPagination?: boolean;
  caption?: string;
  emptyMessage?: React.ReactNode;
  optionalClass?: string;
  firstColumnAlign?: 'left' | 'center' | 'right';
}

const TableRoot: React.FC<RootTableProps> = ({
  header,
  footer,
  children,
  hasPagination,
  caption,
  emptyMessage = <em>No results found</em>,
  optionalClass,
}) => {
  const headCells: { content: ReactNode; className?: string; style?: React.CSSProperties }[] = [];
  const bodyRows: { key?: React.Key; onClick?: React.MouseEventHandler<HTMLTableRowElement> | (() => void); cells: ReactNode[] }[] = [];
  const prebuiltRows: React.ReactElement[] = []; // NEW: arbitrary children get pushed here

  const topLevel = React.Children.toArray(children) as React.ReactElement[];

  topLevel.forEach((child) => {
    if (!child) return;

    const part = (child.type as WithTablePart<any>)[TABLE_PART];

    if (!part) {
      prebuiltRows.push(child);
      return;
    }

    if (part === 'HEAD') {
      const headChildren = React.Children.toArray(child.props?.children) as React.ReactElement[];
      headChildren.forEach((h) => {
        const hPart = (h.type as WithTablePart<any>)[TABLE_PART];
        if (hPart === 'HEAD_ROW') {
          const cells = React.Children.toArray(h.props?.children) as React.ReactElement[];
          cells.forEach((c) => {
            const cPart = (c.type as WithTablePart<any>)[TABLE_PART];
            if (cPart === 'HEAD_CELL') {
              headCells.push({
                content: c.props?.children,
                className: c.props?.className,
                style: c.props?.style,
              });
            } else {
              headCells.push({ content: c });
            }
          });
        } else if (hPart === 'HEAD_CELL') {
          headCells.push({
            content: h.props?.children,
            className: h.props?.className,
            style: h.props?.style,
          });
        } else {
          headCells.push({ content: h });
        }
      });
    }

    if (part === 'BODY') {
      const rows = React.Children.toArray(child.props?.children) as React.ReactElement[];
      rows.forEach((r) => {
        const rPart = (r.type as WithTablePart<any>)[TABLE_PART];

        if (!rPart) {
          prebuiltRows.push(r);
          return;
        }

        if (rPart === 'ROW') {
          const cells = React.Children.toArray(r.props?.children);
          bodyRows.push({
            key: r.key,
            onClick: r.props?.onClick,
            cells,
          });
        } else {
          const cells = React.Children.toArray(r.props?.children ?? r);
          bodyRows.push({ key: r.key, onClick: undefined, cells });
        }
      });
    }
  });

  const hasHead = headCells.length > 0;
  const hasBody = bodyRows.length > 0 || prebuiltRows.length > 0;

  return (
    <>
      {header && <div>{header}</div>}

      <STable hasFooter={!!footer} hasPagination={!!hasPagination} aria-label={caption} optionalClass={ `custom-table-style ${optionalClass}`}>

        {hasHead && (
          <STableHeader>
            {headCells.map((cell, idx) => (
              <STableHeaderItem key={idx} optionalClass={cell.className}>
                {cell.content}
              </STableHeaderItem>
            ))}
          </STableHeader>
        )}

        <STableBody>
          {bodyRows.map((row, rIdx) => (
            <STableRow key={row.key ?? `built-${rIdx}`} onClick={row.onClick as any}>
              {row.cells}
            </STableRow>
          ))}

          {prebuiltRows.map((node, i) => (
            <React.Fragment key={(node.key as any) ?? `prebuilt-${i}`}>{node}</React.Fragment>
          ))}

          {!hasBody && hasHead && (
            <STableRow>
              <SlotCell colSpan={headCells.length} style={{ padding: '0.75rem' }}>
                {emptyMessage}
              </SlotCell>
            </STableRow>
          )}
        </STableBody>
      </STable>
      {footer && (
        <div>{footer}</div>
      )}
    </>
  );
};

const TableWithStatics = Object.assign(TableRoot, {
  Head: Object.assign(SlotHead, {
    Row: SlotHeadRow,
    Cell: SlotHeadCell,
  }),
  Body: SlotBody,
  Row: SlotRow,
  Cell: SlotCell,
});

export const Table: React.FC<RootTableProps> & {
  Head: React.FC<{ children?: ReactNode }> & {
    Row: React.FC<{ children?: ReactNode }>;
    Cell: React.FC<{ children?: ReactNode; className?: string; style?: React.CSSProperties }>;
  };
  Body: React.FC<{ children?: ReactNode }>;
  Row: React.FC<{ children?: ReactNode; onClick?: React.MouseEventHandler<HTMLTableRowElement>; verticalAlign?: ('top' | 'middle' | 'bottom') | (() => void) }>;
  Cell: React.FC<{ children?: ReactNode; colSpan?: number; className?: string; style?: React.CSSProperties }>;
} = TableWithStatics;

export default Table;